import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';
import Notification from '@project/components/Notification/Notification';
import NoAccessResourcesModal from '@project/components/NoAccessResourcesModal/NoAccessResourcesModal';
import { redirectCreateAccountLink, redirectLoginLink } from '@project/helpers/utilityFuncions';
import MessageModalModal, { MessageModalModalType } from '@project/components/MessageModal/MessageModal';

interface Notification {
  id: number;
  message: string;
  type: string;
  position: string;
}

interface NotificationContextType {
  showNotification: (msg: string, pos?: string) => void;
  showMessage: (type: MessageModalModalType, header: any, content: any, hide_in?: number) => void;
  showNoAccess: (authenticated?: boolean, element_group_resource_id?: string) => void;
}

const defaultContextValue: NotificationContextType = {
  showNotification: () => {},
  showNoAccess: () => {},
  showMessage: () => {},
};

const NotificationContext = createContext<NotificationContextType>(defaultContextValue);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNoAccessModal, setShowNoAccessModal] = useState<boolean>(false);
  const [noAccessAuthenticated, setNoAccessAuthenticated] = useState<boolean>(false);
  const [redirectTo, setRedirectTo] = useState<any>(null);

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [modalMessageHeader, setModalMessageHeader] = useState<any>('');
  const [modalMessageContent, setModalMessageContent] = useState<any>('');
  const [modalMessageType, setModalMessageType] = useState<MessageModalModalType>(MessageModalModalType.success);

  const showNoAccess = useCallback((authenticated: boolean, redirect_to: string) => {
    setShowNoAccessModal(true);
    setNoAccessAuthenticated(authenticated);
    setRedirectTo(redirect_to);
  }, []);

  const showNotification = useCallback((msg: string, type: string, pos: string = 'top-right') => {
    const id = new Date().getTime(); // Unique identifier for each notification
    setNotifications((prev) => [...prev, { id, message: msg, type, position: pos }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    }, 3300); // Duration for each notification plus fade-out time
  }, []);

  const noAccessLink = () => {
    return !noAccessAuthenticated ? redirectLoginLink(redirectTo) : redirectTo;
  };
  const noAccessLink2 = () => {
    return !noAccessAuthenticated ? redirectCreateAccountLink(redirectTo) : redirectTo;
  };

  const showMessage = useCallback(
    (type: MessageModalModalType = MessageModalModalType.success, header: any, content: any, hide_in: number) => {
      setShowMessageModal(true);
      setModalMessageType(type);
      setModalMessageContent(content);
      setModalMessageHeader(header);
      if (hide_in) {
        setTimeout(() => {
          if (showMessageModal) setShowMessageModal(false);
        }, hide_in);
      }
    },
    []
  );

  // Group notifications by position
  const notificationsByPosition = notifications.reduce(
    (acc, notification) => {
      if (!acc[notification.position]) {
        acc[notification.position] = [];
      }
      acc[notification.position].push(notification);
      return acc;
    },
    {} as Record<string, Notification[]>
  );

  return (
    <NotificationContext.Provider value={{ showNotification, showNoAccess, showMessage }}>
      {children}
      <div>
        <MessageModalModal
          show={showMessageModal}
          content={modalMessageContent}
          header={modalMessageHeader}
          type={modalMessageType}
          onClose={() => setShowMessageModal(false)}
        />
        <NoAccessResourcesModal
          show={showNoAccessModal}
          authenticated={noAccessAuthenticated}
          link1={noAccessLink()}
          link2={noAccessLink2()}
          onClose={() => setShowNoAccessModal(false)}
        />
      </div>
      {Object.keys(notificationsByPosition).map((pos) => (
        <div key={pos} className={`notification-container ${pos}`}>
          {notifications.map((notification) => (
            <Notification
              key={notification.id}
              message={notification.message}
              type={notification.type}
              position={notification.position}
              visible={true}
              onClose={() => setNotifications((prev) => prev.filter((n) => n.id !== notification.id))}
            />
          ))}
        </div>
      ))}
    </NotificationContext.Provider>
  );
};
